/**
 * StateComponents - Loading/Error/Empty 状态组件
 */

import { useLanguage } from '../hooks/useLanguage'

// Loading 状态
export function LoadingState({ message = '加载中...' }: { message?: string }) {
  const { t } = useLanguage()
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <svg className="animate-spin w-8 h-8 text-blue-500 mb-3" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
      <p className="text-gray-500 dark:text-gray-400 text-sm">
        {message === '加载中...' ? t('加载中...', 'Loading...') : message}
      </p>
    </div>
  )
}

// Error 状态
export function ErrorState({
  message = '加载失败',
  onRetry
}: {
  message?: string
  onRetry?: () => void
}) {
  const { t } = useLanguage()
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="text-4xl mb-3">⚠️</div>
      <p className="text-red-500 dark:text-red-400 text-sm mb-4">
        {message === '加载失败' ? t('加载失败', 'Failed to load') : message}
      </p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors"
        >
          {t('重新加载', 'Reload')}
        </button>
      )}
    </div>
  )
}

// Empty 状态
export function EmptyState({
  message = '暂无数据',
  description
}: {
  message?: string
  description?: string
}) {
  const { t } = useLanguage()
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="text-4xl mb-3">📭</div>
      <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">
        {message === '暂无数据' ? t('暂无数据', 'No data') : message}
      </p>
      {description && (
        <p className="text-gray-400 dark:text-gray-500 text-xs">{description}</p>
      )}
    </div>
  )
}

// 详情页 Loading
export function DetailLoadingState() {
  const { t } = useLanguage()
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--tg-bg-color,#ffffff)]">
      <div className="text-center">
        <svg className="animate-spin w-12 h-12 text-blue-500 mb-4 mx-auto" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <p className="text-gray-500">{t('正在加载事件详情...', 'Loading event details...')}</p>
      </div>
    </div>
  )
}

// 详情页 Error
export function DetailErrorState({
  message = '事件不存在',
  onBack
}: {
  message?: string
  onBack?: () => void
}) {
  const { t } = useLanguage()
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--tg-bg-color,#ffffff)]">
      <div className="text-center">
        <div className="text-5xl mb-4">🔍</div>
        <p className="text-red-500 mb-4">
          {message === '事件不存在' ? t('事件不存在', 'Event not found') : message}
        </p>
        {onBack && (
          <button
            onClick={onBack}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            {t('返回列表', 'Back to List')}
          </button>
        )}
      </div>
    </div>
  )
}
